import React from 'react';
import { Input, Tag } from "antd";
import { useTranslation } from "react-i18next";
import { FoldableTagList } from 'components/List';
import { RiskModel } from 'model/RiskModel';

interface ProjectTag {
    id: string;
    name: string;
}

interface Risk {
    id: string;
    name: string;
    isOpportunity: boolean;
}

interface FiltersTabProps {
    searchTerm: string;
    setSearchTerm: (term: string) => void;
    risks: Risk[];
    riskFilter: string | null;
    setRiskFilter: (risks: string | null) => void;
    opportunitiesFilter: string | null;
    setOpportunitiesFilter: (opportunities: string | null) => void;
    displayRisks: boolean;
    tags?: ProjectTag[];
    usedTags?: string[];
    tagFilter?: string[];
    setTagFilter?: (tags: string[]) => void;
    select?: (item: any) => void;
}

export const FiltersTab: React.FC<FiltersTabProps> = (props) => {
    const { t } = useTranslation();
    const {
        risks = [],
        riskFilter = null,
        opportunitiesFilter = null,
        tags = [],
        usedTags = [],
        tagFilter = [],
    } = props;

    const allRisks = risks.filter(r => !r.isOpportunity);
    const allOpportunities = risks.filter(r => r.isOpportunity);

    let usedRisks = {};
    risks.forEach((r: RiskModel) => {
        if (r.impactedStakes) {
            r.impactedStakes.forEach((i) => {
                if (i.stakeId)
                    usedRisks[i.riskId] = true;
            })
        }
    })

    let usedOpportunities = {};
    risks.forEach((r: RiskModel) => {
        if (r.impactedStakes) {
            r.impactedStakes.forEach((i) => {
                if (i.stakeId)
                    usedOpportunities[i.riskId] = true;
            })
        }
    })

    return (
        <div className="FiltersTab">
            <Input
                onChange={(e) => {
                    props.setSearchTerm(e.target.value);
                }}
                value={props.searchTerm}
                placeholder={t('messages.search')}
                className="SearchInput w-100 mt-05"
            />

            {props.setTagFilter && (
                <>
                    <strong className="text-left mt-2" style={{ marginBottom: '0.5rem' }}>
                        {t('pages.navigator.all_tags')}
                    </strong>
                    <div className="text-left" style={{ marginBottom: '0.5rem' }}>
                        {tags.length === 0 ? (
                            <div style={{ color: 'var(--ant-color-text-tertiary)', fontSize: '0.85rem' }}>
                                {t('pages.navigator.no_tag')}
                            </div>
                        ) : (
                            tags.map((tag) => (
                                <Tag.CheckableTag
                                    key={tag.id}
                                    checked={tagFilter.includes(tag.id)}
                                    onClick={() => {
                                        const next = tagFilter.includes(tag.id)
                                            ? tagFilter.filter(id => id !== tag.id)
                                            : [...tagFilter, tag.id];
                                        props.setTagFilter!(next);
                                    }}
                                    className="tag"
                                >
                                    {tag.name}
                                </Tag.CheckableTag>
                            ))
                        )}
                    </div>
                </>
            )}

            {props.displayRisks && (
                <>
                    <FoldableTagList
                        title={t('pages.navigator.all_risks')}
                        tags={allRisks}
                        usedTags={usedRisks}
                        selectedTags={riskFilter ? [riskFilter] : []}
                        emptyMessage={t('models.risk.messages.no_x')}
                        className="ImpactedRisk"
                        onTagClick={(riskId) => {
                            riskFilter === riskId
                                ? props.setRiskFilter(null)
                                : props.setRiskFilter(riskId);
                            props.select?.(null);
                        }}
                    />
                    <div className="mt-2"></div>

                    <FoldableTagList
                        title={t('pages.navigator.all_opportunities')}
                        tags={allOpportunities}
                        usedTags={usedOpportunities}
                        selectedTags={opportunitiesFilter ? [opportunitiesFilter] : []}
                        emptyMessage={t('models.risk.messages.no_x_positive')}
                        className="ImpactedRisk"
                        onTagClick={(riskId) => {
                            opportunitiesFilter === riskId
                                ? props.setOpportunitiesFilter(null)
                                : props.setOpportunitiesFilter(riskId);
                            props.select?.(null);
                        }}
                    />
                </>
            )}
        </div>
    );
};
